import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { Tooltip as MuiTooltip } from '@mui/material';
import If from '@components/if/if.tsx';
import InfoTooltip from '@components/info-tooltip/info-tooltip.tsx';
import ChartLoader from '@shared/components/charts/chart-loader/chart-loader.tsx';
import ChartEmpty from '@shared/components/charts/chart-empty/chart-empty.tsx';
import LegendBarChartTooltip from '@shared/components/charts/legend-bar-chart/components/legend-bar-chart-tooltip/legend-bar-chart-tooltip.tsx';
import { LegendBarChartProps } from '@shared/models/charts/legend-bar-chart.model.ts';

const LegendBarChart: React.FC<LegendBarChartProps> = ({
  data,
  summaries,
  loading = false,
  config
}) => {
  const { t } = useTranslation();
  const {
    height = 280,
    barSize = 25,
    barRadius = [30, 30, 30, 30],
    showGrid = true,
    yAxisFormatter,
    tooltipValueLabel
  } = config;

  // recharts reads a per-bar `fill` straight from the datum (see Bar.js
  // `barRectangleItem`), so mapping `color → fill` replaces the deprecated
  // <Cell> children while keeping identical colors.
  const barData = data.map((item) => ({ ...item, fill: item.color }));

  return (
    <div
      className="bg-(--white) rounded-xl p-4 overflow-hidden relative"
      style={{
        boxShadow: '0 4px 12px var(--black-alpha-50)'
      }}
    >
      <If
        loading={loading}
        condition={!loading && data.length > 0}
        loadingContent={<ChartLoader height={height} />}
        emptyContent={<ChartEmpty height={height} />}
      >
        <div className="flex items-center" style={{ minHeight: height }}>
          <div className="flex flex-col gap-4 pr-1 shrink-0">
            {data.map((item, index) => (
              <div
                key={`legend-${index}`}
                className="flex items-center gap-2 pl-2"
              >
                <div
                  className="w-3.75 h-3.75 rounded-full shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <MuiTooltip title={item?.name} followCursor>
                  <span className="text-[15px] font-normal text-(--blue-4) max-w-50 truncate block">
                    {item.name}
                  </span>
                </MuiTooltip>
              </div>
            ))}
          </div>

          <div className="flex-1 flex flex-col">
            {summaries && summaries.length > 0 && (
              <div className="flex items-center justify-end gap-6 mb-4 mr-7">
                {summaries.map((summary, index) => (
                  <div key={`summary-${index}`} className="flex items-center">
                    <span className="text-[20px] font-bold text-(--blue-4) max-lg-xl:text-base">
                      {summary.label}
                    </span>
                    <span
                      className="text-[20px] font-semibold ml-1 max-lg-xl:text-base"
                      style={{
                        color: summary.valueColor || 'var(--blue-4)'
                      }}
                    >
                      {summary.value}
                    </span>
                    {summary.suffix && (
                      <span
                        className="text-[25px] font-bold ml-4 max-lg-xl:text-xl"
                        style={{
                          color: summary.suffixColor || 'var(--blue-4)'
                        }}
                      >
                        {summary.suffix}
                      </span>
                    )}
                    {summary.infoTooltip && (
                      <InfoTooltip text={summary.infoTooltip} />
                    )}
                  </div>
                ))}
              </div>
            )}

            <ResponsiveContainer width="100%" height={height}>
              <RechartsBarChart
                data={barData}
                margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
              >
                {showGrid && (
                  <CartesianGrid vertical={false} stroke="var(--gray-7)" />
                )}
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={false}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'var(--blue-4)', fontSize: 14 }}
                  tickFormatter={yAxisFormatter}
                />
                <Tooltip
                  content={
                    <LegendBarChartTooltip
                      tooltipValueLabel={tooltipValueLabel}
                      t={t}
                    />
                  }
                />
                <Bar dataKey="value" radius={barRadius} barSize={barSize} />
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </If>
    </div>
  );
};

export default LegendBarChart;
